// ProjectSetup.jsx — Section 1
import React, { useState, useEffect } from 'react'
import * as api from '../../services/api.js'
import Card from '../../components/Card.jsx'
import TipBox from '../../components/TipBox.jsx'
import AppSelect from '../../components/AppSelect.jsx'
import GenrePicker from '../../components/GenrePicker.jsx'
import { getGenreIcon } from '../../utils/genres.js'

const LENGTH_OPTIONS = [
  { value: '5000',   label: 'Short story (up to 5,000 words)' },
  { value: '20000',  label: 'Novella (20,000–40,000 words)' },
  { value: '60000',  label: 'Novel (60,000–90,000 words)' },
  { value: '100000', label: 'Epic novel (100,000+ words)' },
]

export default function ProjectSetup({ pid = 'w' }) {
  const [project, setProject]     = useState({})
  const [showPicker, setShowPicker] = useState(false)

  useEffect(() => { api.getWritingProjectData(pid).then(d => setProject(d || {})) }, [pid])

  function set(field, value) {
    setProject(prev => {
      const next = { ...prev, [field]: value }
      api.saveWritingProjectData(pid, next)
      return next
    })
  }

  return (
    <Card label="Section 1" title="Your project">
      <TipBox title="💡 Getting started" collapsible>
        <p>Answer three questions before you write a word: <em>What kind of story is this? Who is it for? What do I want to feel when it's done?</em></p>
      </TipBox>

      {/* Genre chips display */}
      {project.genre && (
        <div className="project-genre-tags" style={{ marginTop: '1rem' }}>
          <span className="genre-chip">{getGenreIcon(project.genre)} {project.genre}</span>
          {project.subgenre && <span className="subgenre-chip">{project.subgenre}</span>}
        </div>
      )}

      <div className="w-form" style={{ marginTop: '1.25rem' }}>
        <div className="full">
          <label className="w-label">Working title</label>
          <input className="w-input" placeholder="Your story's working title..." value={project.title || ''} onChange={e => set('title', e.target.value)} />
        </div>

        {/* Genre picker */}
        <div className="full">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <label className="w-label" style={{ margin: 0 }}>Genre</label>
            {project.genre && (
              <button
                className="ghost-btn"
                style={{ fontSize: '0.72rem', padding: '2px 8px' }}
                onClick={() => setShowPicker(v => !v)}
              >
                {showPicker ? 'Done' : 'Change'}
              </button>
            )}
          </div>
          {(!project.genre || showPicker) ? (
            <GenrePicker
              genre={project.genre || ''}
              subgenre={project.subgenre || ''}
              onGenreChange={v => { set('genre', v); set('subgenre', '') }}
              onSubgenreChange={v => { set('subgenre', v); setShowPicker(false) }}
            />
          ) : (
            <button
              className="ghost-btn"
              style={{ fontSize: '0.82rem', width: '100%', textAlign: 'left' }}
              onClick={() => setShowPicker(true)}
            >
              {getGenreIcon(project.genre)} {project.genre}{project.subgenre ? ` · ${project.subgenre}` : ''}
            </button>
          )}
        </div>

        <div>
          <label className="w-label">Target length</label>
          <AppSelect
            value={project.length || ''}
            onChange={v => set('length', v)}
            options={LENGTH_OPTIONS}
            placeholder="Choose a length..."
          />
        </div>
        <div className="full">
          <label className="w-label">One-line premise</label>
          <input className="w-input" placeholder="e.g. A grieving librarian discovers she can enter the lives she never lived..." value={project.premise || ''} onChange={e => set('premise', e.target.value)} />
        </div>
        <div className="full">
          <label className="w-label">Core themes</label>
          <textarea className="w-textarea" rows={2} placeholder="e.g. Grief, second chances, the lives we choose not to live..." value={project.themes || ''} onChange={e => set('themes', e.target.value)} />
        </div>
        <div className="full">
          <label className="w-label">Your intention (how do you want readers to feel?)</label>
          <textarea className="w-textarea" rows={2} placeholder="e.g. Hopeful but a little undone..." value={project.intention || ''} onChange={e => set('intention', e.target.value)} />
        </div>
      </div>
      <TipBox title="Questions to ask yourself" collapsible>
        <ul>
          <li>Is my premise specific enough that I could describe it in one sentence to a stranger?</li>
          <li>What is the emotional journey of my protagonist — not the plot events, but how they feel differently at the end than at the beginning?</li>
          <li>If a reader finishes this book and remembers only one thing, what do I want it to be?</li>
          <li>What am I personally trying to work out by writing this story?</li>
        </ul>
      </TipBox>
    </Card>
  )
}
